import React, { useState } from "react";
import { useLocation } from "react-router";
import { makeStyles } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import Hidden from "@material-ui/core/Hidden";
import { AdminHtmlState } from "src/interfaces/adminHtmlState.interface";
import AdminHeader from "src/components/AdminHeader/Support";
import Navigator from "src/components/Navigator/Support";

import { drawerWidth, drawerWidthCollapsed, type Theme } from "src/theme";
import { useSelector, useDispatch } from "react-redux";
import { toggleMobileOpen } from "src/store/adminHtml/actions";
import { AppConfig } from "src/config";
import { navigation } from "src/config/supportNavigation";

const useStyles = makeStyles((theme: Theme) => {
  const { custom: palette } = theme.palette.custom;

  return {
    root: {
      display: "flex",
      minHeight: "100vh",
      backgroundColor: palette.bg,
    },
    drawer: {
      [theme.breakpoints.up("sm")]: {
        width: drawerWidth,
        flexShrink: 0,
      },
    },
    appContent: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      backgroundColor: palette.bg,
    },
  };
});

function Support({ children }) {
  const location = useLocation();
  const classes = useStyles();
  const mobileOpen = useSelector(
    (state: { adminHtml: AdminHtmlState }) => state.adminHtml.mobileOpen
  );
  const dispatch = useDispatch();
  const onDrawerToggle = () => {
    dispatch(toggleMobileOpen());
  };
  const activePage = navigation.find((e) =>
    location.pathname.startsWith(e.href)
  );
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("nav-collapsed");
    return saved === "true";
  });

  const handleToggleCollapse = () => {
    setCollapsed((prev) => {
      const newValue = !prev;
      localStorage.setItem("nav-collapsed", String(newValue));
      // Trigger window resize after transition completes so charts resize
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 350);
      return newValue;
    });
  };

  const currentDrawerWidth = collapsed ? drawerWidthCollapsed : drawerWidth;

  return (
    <div className={classes.root}>
      <CssBaseline />
      <nav
        className={classes.drawer}
        style={{ width: currentDrawerWidth, transition: "width 0.3s ease" }}
      >
        <Hidden smUp implementation="js">
          <Navigator
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={mobileOpen}
            onClose={onDrawerToggle}
            title={AppConfig.title}
            subTitle="Dashboard"
          />
        </Hidden>
        <Hidden xsDown implementation="css">
          <Navigator
            PaperProps={{
              style: {
                width: currentDrawerWidth,
                transition: "width 0.3s ease",
                overflow: "visible",
              },
            }}
            open={mobileOpen}
            variant="permanent"
            title={AppConfig.title}
            subTitle="Dashboard"
            collapsed={collapsed}
            onToggleCollapse={handleToggleCollapse}
          />
        </Hidden>
      </nav>
      <div
        className={classes.appContent}
        style={{
          marginLeft: 0,
          width: `calc(100% - ${currentDrawerWidth}px)`,
          transition: "width 0.3s ease",
        }}
      >
        <AdminHeader
          onDrawerToggle={onDrawerToggle}
          title={activePage?.title || "Support"}
        />
        {children}
      </div>
    </div>
  );
}

export default Support;
